import Link from 'next/link'
import { FC, useEffect, useMemo } from 'react'

import { Dropdown, Button as LMFButton } from '@syconium/little-miss-figgy'

import { useLocalization } from '../../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { AddToBagButton } from '../../../../../brunswick/components/AddToBagButton'
import { trackEvent } from '../../../../../lib/analytics'
import { useSelectVariant } from '../../../../../lib/hooks/useSelectVariant'
import { useProductHrefWithQuery } from '../../../../../lib/routing'
import { deriveAvailableProducts, intlPrettyCurrency } from '../../../../../lib/utils'
import { ProductWithAvailability } from '../../../../../types/graphql'

import * as Styles from './styles'
import useFetchCompleteTheSet from './useFetchCompleteTheSet'
import useFetchDefault from './useFetchDefault'

export type CompleteTheSetProps = Readonly<{
  productHandle: string
  completeTheSetAutofill: boolean
}>

export type CompleteTheSetDetailsProps = Readonly<{
  product: ProductWithAvailability
}>

export const CompleteTheSet: FC<CompleteTheSetProps> = ({
  productHandle,
  completeTheSetAutofill = false,
}) => {
  const fetchCall = completeTheSetAutofill ? useFetchDefault : useFetchCompleteTheSet
  const { product, loading } = fetchCall(productHandle)

  if (loading || !product) {
    return null
  }

  return <CompleteTheSetDetails product={product} />
}

const CompleteTheSetDetails: FC<CompleteTheSetDetailsProps> = ({ product }) => {
  const availableProducts = useMemo(() => {
    return deriveAvailableProducts(product.productGroup.productsForAvailability)
  }, [product.productGroup.productsForAvailability])
  const variant = useSelectVariant({
    availableProducts,
    coloredAndFitVariants: product.variants ?? [],
    imageSize: 150,
    initialFeaturedProduct: product,
    initialSelectedColor: product.colorInfo,
    initializeWithDefault: true,
    productGroup: product.productGroup,
  })
  const {
    imageSource,
    sizes,
    availableFitsKeys,
    category,
    ctaButton,
    price,
    setSelectedFitKey,
    setSelectedSize,
    currentFitKey,
    currentSize,
  } = variant
  const {
    selectAndAddVariant: strings,
    completeTheSet,
    detailsSection,
    favorites,
    pages: { product: productPageStrings },
  } = useTranslation()
  const { region, locale } = useLocalization()

  useEffect(() => {
    const firstAvailableSize = sizes.find(size => !size.soldOut)
    if (firstAvailableSize) setSelectedSize(firstAvailableSize)
  }, [currentFitKey, setSelectedSize, sizes])

  const sizeOptions = useMemo(
    () =>
      sizes.map(size => {
        const colorKey = product.color
        const fitSizes = colorKey ? availableProducts[colorKey]?.[currentFitKey] : undefined
        return fitSizes?.includes(size.shorthand)
          ? size.shorthand
          : `${size.shorthand} (${detailsSection.soldOutSectionTitleMessage})`
      }),
    [availableProducts, product.color, currentFitKey, sizes, detailsSection]
  )

  const handleOnSizeChange = (value: string) => {
    const selected = sizes.find(s => s.shorthand === value)
    if (selected) setSelectedSize(selected)
  }

  const productHref = useProductHrefWithQuery({
    color: product?.colorInfo?.name ?? '',
    handle: product.productGroup.handle,
  })

  const fitSectionLabel =
    category === 'Scrub Top'
      ? productPageStrings.scrubTopsFitTitle
      : category === 'Scrub Pants'
      ? productPageStrings.scrubPantsFitTitle
      : favorites.os

  return (
    <Styles.CompleteTheSetWrap>
      <Styles.Title asTag='h3'>{completeTheSet.headline}</Styles.Title>
      <Link href={productHref}>
        <Styles.Details>
          {imageSource && (
            <Styles.Thumbnail
              alt={variant.productGroupTitle}
              loading='lazy'
              src={imageSource}
              widths={{
                unit: 'px',
                sm: 150,
                md: 150,
              }}
              aspectRatios={{
                sm: 1,
                md: 1,
              }}
            ></Styles.Thumbnail>
          )}
          <Styles.DetailsText>
            <Styles.ProductName>{variant.productGroupTitle}</Styles.ProductName>
            <Styles.ColorText>{product.color}</Styles.ColorText>
          </Styles.DetailsText>
        </Styles.Details>
      </Link>
      <Styles.SelectWrap $fullWidth={!availableFitsKeys.length}>
        {!!sizeOptions.length && (
          <Dropdown
            placeholderText={strings.sizeSection.title}
            selection={currentSize?.shorthand}
            options={sizeOptions}
            onChange={handleOnSizeChange}
            {...trackEvent({
              action: 'change size',
              category: 'complete the set',
              value: product.handle || '',
            })}
          />
        )}
        {!!availableFitsKeys.length && (
          <Dropdown
            placeholderText={fitSectionLabel}
            selection={currentFitKey}
            options={availableFitsKeys}
            onChange={setSelectedFitKey}
            {...trackEvent({
              action: 'change fit',
              category: 'complete the set',
              value: product.handle || '',
            })}
          />
        )}
      </Styles.SelectWrap>
      <AddToBagButton
        ButtonComponent={LMFButton}
        allCaps
        block
        size={'large'}
        disabled={ctaButton.disabled}
        onClick={ctaButton.onClick}
        variant={'ghost'}
        {...trackEvent({
          action: 'add to bag',
          category: 'complete the set',
          value: product.handle || '',
        })}
      >
        {`${intlPrettyCurrency({ cents: price, truncateTrailingZeros: true, region, locale })} • ${
          strings.addToBagButton.cta
        }`}
      </AddToBagButton>
    </Styles.CompleteTheSetWrap>
  )
}

export default CompleteTheSet
